// useUsers — data hooks for the User Management page

import { usePolling } from "./useDashboard";
import { apiFetch } from "../lib/apiFetch";

// ── Types ─────────────────────────────────────────────────────────────────────

export type AppRole = "admin" | "board_member" | "viewer";

export interface UserRow {
  id: number;
  email: string;
  name: string;
  role: AppRole;
  is_active: number;
  created_at: string | null;
  last_login_at: string | null;
  updated_at: string | null;
}

export interface UserStats {
  total: number;
  admins: number;
  active_last_30d: number;
  inactive: number;
}

export interface UsersResponse {
  users: UserRow[];
  count: number;
  stats: UserStats;
}

export interface UserActivityEntry {
  id: number;
  email: string;
  name: string;
  role: AppRole;
  last_login_at: string | null;
}

export interface UserActivityResponse {
  recent_logins: UserActivityEntry[];
  active_sessions: UserActivityEntry[];
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** Poll GET /api/users every `interval` ms (default 60 s). Admin-only. */
export function useUsers(interval?: number) {
  return usePolling<UsersResponse>("/api/users", interval ?? 60_000);
}

/** Poll GET /api/users/activity every `interval` ms (default 60 s). Admin-only. */
export function useUserActivity(interval?: number) {
  return usePolling<UserActivityResponse>(
    "/api/users/activity",
    interval ?? 60_000,
  );
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/** PATCH /api/users/:id — update role or is_active */
export async function updateUser(
  id: number,
  updates: { role?: AppRole; is_active?: boolean },
): Promise<{ user: UserRow }> {
  const res = await apiFetch(`/api/users/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to update user (HTTP ${res.status})`);
  }
  return res.json() as Promise<{ user: UserRow }>;
}

/** DELETE /api/users/:id — soft-deactivate */
export async function deactivateUser(
  id: number,
): Promise<{ deactivated: boolean; id: number }> {
  const res = await apiFetch(`/api/users/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `Failed to deactivate user (HTTP ${res.status})`,
    );
  }
  return res.json() as Promise<{ deactivated: boolean; id: number }>;
}
